import React, { useId, useRef } from 'react';
import { IconButton } from '@noths/polaris-design-system-core-components';

import { useCarouselNavigation } from '../../../hooks/useCarouselNavigation';
import { useEdgeVisibility } from '../../../hooks/useEdgeVisibility';
import { ProductCard } from '../../molecules/ProductCard/ProductCard';
import type { ProductCardClickPayload } from '../../molecules/ProductCard/types';
import { ProductCardSkeleton } from '../../molecules/ProductCardSkeleton/ProductCardSkeleton';
import type { ProductCarouselItem, ProductCarouselProps } from './types';
import { useItemImpressions } from './useItemImpressions';
import { useOnVisible } from './useOnVisible';
import { withSingleImage } from './withSingleImage';
import * as styles from './ProductCarousel.styles';

export const PRODUCT_CAROUSEL_TEST_ID = 'product-carousel';

/* How many placeholders stand in for a set whose size is not yet known. Enough
   to fill the track on a wide container, which is where an under-filled loading
   state would be most obvious. */
const SKELETON_ITEM_COUNT = 6;

export const ProductCarousel = ({
  heading,
  itemSize = 'auto',
  onNavigate,
  onProductClick,
  onProductVisible,
  onVisible,
  products,
  subheadingLink,
}: ProductCarouselProps) => {
  const headingId = useId();
  const trackId = useId();
  const carouselRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);

  const isLoading = products === null;
  const items = products ?? [];

  /* Every hook runs before the empty-set return below, so the hook order is the
     same in all three states. */
  const { isAtEnd, isAtStart } = useEdgeVisibility(trackRef, items.length);
  const { scrollNext, scrollPrev } = useCarouselNavigation(trackRef, onNavigate);

  useOnVisible(carouselRef, onVisible, { enabled: !isLoading });
  useItemImpressions(
    trackRef,
    onProductVisible && ((index) => onProductVisible({ index, product: items[index].product })),
    items.length,
  );

  /**
   * The card reports its own clicks, so the carousel only adds what the card
   * cannot know: where in the set the product was.
   *
   * An item's own `onProductClick` still runs. `ProductCarouselItem` is the
   * card's props, so a consumer may set one per item, and a carousel-level
   * handler should add to that rather than replace it.
   */
  const handleProductClick =
    (item: ProductCarouselItem, index: number) =>
    (e: React.MouseEvent<HTMLAnchorElement>, payload: ProductCardClickPayload) => {
      item.onProductClick?.(e, payload);
      onProductClick?.(e, { index, product: item.product });
    };

  /* Nothing to show and nothing coming. Rendering an empty heading would be
     worse than rendering nothing at all. */
  if (!isLoading && items.length === 0) {
    return null;
  }

  return (
    <section
      aria-busy={isLoading || undefined}
      aria-labelledby={heading ? headingId : undefined}
      aria-roledescription="carousel"
      css={styles.carousel}
      data-testid={PRODUCT_CAROUSEL_TEST_ID}
      ref={carouselRef}
    >
      {/* Always rendered, because the arrows live here and a carousel is no less
          scrollable for having no heading. */}
      <header css={styles.header}>
        <div css={styles.headingGroup}>
          {heading && (
            <h2 css={styles.heading} id={headingId}>
              {heading}
            </h2>
          )}
          {/* Loading, with no heading given. A bar holds the space a heading
              would take, so the track does not move up the page when one
              arrives above it. */}
          {!heading && isLoading && <span aria-hidden css={styles.headingSkeleton} />}
          {subheadingLink && (
            <a
              css={styles.subheadingLink}
              href={subheadingLink.href}
              onClick={subheadingLink.onClick}
            >
              {subheadingLink.text}
            </a>
          )}
        </div>

        {/* Always rendered, and disabled rather than unmounted whenever there is
            nothing to scroll to, whether that is because the track is at an end
            or because the products have not arrived yet. The arrows are taller
            than the heading, so removing them would shorten the header, and the
            track would step down the page the moment they came back. That is
            the layout shift the loading state exists to avoid. */}
        <div aria-label="Carousel controls" css={styles.controls} role="group">
          <IconButton
            aria-controls={trackId}
            aria-label="Previous products"
            compact
            disabled={isLoading || isAtStart}
            icon="actionBack"
            onClick={scrollPrev}
          />
          <IconButton
            aria-controls={trackId}
            aria-label="Next products"
            compact
            disabled={isLoading || isAtEnd}
            icon="actionForward"
            onClick={scrollNext}
          />
        </div>
      </header>

      {/* The step is set here rather than on the section because the track is
          what reads the gap and what the items inherit their width from. The
          section carries the container the auto step measures, so the two sit an
          element apart. Always rendered, including for the default, so the
          resolved step is visible in devtools and assertable in a test. */}
      <ul css={styles.track} data-item-size={itemSize} id={trackId} ref={trackRef}>
        {isLoading
          ? Array.from({ length: SKELETON_ITEM_COUNT }).map((_, index) => (
              <li aria-hidden css={styles.item} key={index}>
                <ProductCardSkeleton />
              </li>
            ))
          : items.map((item, index) => (
              <li
                aria-label={`slide ${index + 1} of ${items.length}`}
                aria-roledescription="slide"
                css={styles.item}
                key={item.product.code}
                role="group"
              >
                {/* One image per card in a carousel, whatever the product
                    carries. See withSingleImage for why. A new product object
                    per render, so memoising ProductCard would need this hoisting
                    first; at a carousel's size it is not worth the indirection
                    yet. */}
                <ProductCard
                  {...item}
                  onProductClick={handleProductClick(item, index)}
                  product={withSingleImage(item.product)}
                />
              </li>
            ))}
      </ul>
    </section>
  );
};

ProductCarousel.displayName = 'ProductCarousel';
